import {
  insertFilesSchema,
  insertQuestionOptionsSchema,
  type Question,
  questionSchema,
  type StudySet,
} from "@studygenie/db";
import { z } from "zod";

export const pastedTextSchema = z.object({
  pastedText: z.string().min(200),
});

export type FileStatus = "success" | "pending" | "failed";

const editQuestionSchema = questionSchema
  .pick({
    question: true,
    answer: true,
    questionImageUrl: true,
    answerImageUrl: true,
  })
  .extend({
    id: z.string(),
    answer: z.string().optional(),
    isNew: z.boolean().optional(),
  });

export const questionArrayFormSchema = z.object({
  questions: z
    .array(editQuestionSchema)
    .min(2, { message: "A minimum of 2 questions are required" }),
});

export type QuestionFormValues = z.infer<typeof questionArrayFormSchema>;

export type PlanType = "yearly" | "monthly";

export const fileSchema = insertFilesSchema;

export const questionOptionSchema = insertQuestionOptionsSchema;

export const incorrectQuestionOptionsZodSchema = z
  .array(z.string().min(1, "Option text cannot be empty"))
  .length(3, "Must have exactly 3 options")
  .describe(
    "An array of incorrect answer options for the multiple-choice question. Must include three options.",
  );

// Create the Zod schema properly
export const SubjectSchema = z.enum([
  "Anatomy",
  "Pharmacology",
  "Accounting",
  "Algebra",
  "Art History",
  "Biology",
  "Chemistry",
  "Education",
  "Geography",
  "Geometry",
  "History",
  "Microbiology",
  "Music Theory",
  "Networking",
  "Nursing",
  "Organic Chemistry",
  "Physics",
  "Psychology",
  "US History",
  "Writing",
  "Statistics",
  "Business",
  "Economics",
  "Computer Science",
  "Sociology",
  "Environmental Science",
  "Calculus",
  "World History",
  "Political Science",
  "Philosophy",
  "Astronomy",
  "Software Development",
  "Health Science",
  "Forensic Science",
  "Finance",
  "Marketing",
  "Entrepreneurship",
  "Human Resources",
  "Supply Chain",
  "Communications",
  "Engineering",
]);

// Define an array of subjects using the .array() method
export const SubjectArray = SubjectSchema.options;

export const questionsZodSchema = z.object({
  thoughtProcess: z
    .string()
    .describe(
      "A detailed explanation of the pre-generation planning process for creating questions",
    ),
  subject: SubjectSchema.describe(
    "The subject of the generated quiz questions. If not in the list of options return null.",
  ).nullable(),
  tags: z
    .array(z.string())
    .describe(
      "An array of tags representing extracted keywords, topics, or concepts from the text chunk.",
    ),
  questions: z
    .array(
      z
        .object({
          question: z
            .string()
            .min(1, { message: "Question text cannot be empty" })
            .describe(
              "The text of the versatile question being asked. Must be adaptable for multiple-choice, written response, and flashcard formats without modification.",
            ),
          answer: z
            .string()
            .min(1, { message: "Answer text cannot be empty" })
            .describe("The correct answer to the question."),
          distractors: z
            .array(
              z.string().min(1, {
                message: "Each distractor must contain at least one character.",
              }),
            )
            .length(3, { message: "Must have exactly 3 distractors" })
            .refine(
              (distractors) => new Set(distractors).size === distractors.length,
              {
                message: "All distractors must be unique",
              },
            )
            .describe(
              "An array of 3 plausible incorrect answers for multiple-choice format.",
            ),
          cognitiveLevel: z
            .enum([
              "Knowledge",
              "Comprehension",
              "Application",
              "Analysis",
              "Synthesis",
              "Evaluation",
            ])
            .describe(
              "The cognitive level of the question based on Bloom's Taxonomy.",
            ),
        })
        .refine((q) => !q.distractors.includes(q.answer), {
          message: "Distractors must not include the correct answer",
        }),
    )
    .min(8, { message: "Must have at least 8 questions" })
    .describe(
      "An array of 8-10 versatile questions adaptable for multiple-choice, written response, and flashcard formats.",
    ),
});
export type QuestionsZodSchema = z.infer<typeof questionsZodSchema>;

// Schema for flashcard-oriented fact extraction
export const factsZodSchema = z
  .object({
    source: z
      .object({
        text: z
          .string()
          .describe(
            "The verbatim text excerpt from the source document that validates this question and answer",
          ),
      })
      .describe("Source information with quality assessment"),
    question: z
      .string()
      .min(1, { message: "Question cannot be empty" })
      .describe("The question form of the extracted fact"),
    answer: z
      .string()
      .min(1, { message: "Answer cannot be empty" })
      .describe("Precise, complete answer"),
  })
  .describe("A flashcard-style question and answer pair");

// Type inference from the schema
export type FactsSchema = z.infer<typeof factsZodSchema>;

export const metadataZodSchema = z.object({
  thoughtProcess: z
    .string()
    .describe(
      "A detailed explanation of how the subject and tags were determined from the text",
    ),
  subject: SubjectSchema.nullable().describe(
    "The primary subject area of the text. If none match, returns null",
  ),
  tags: z
    .array(z.string())
    .min(3, { message: "Must provide at least 3 tags" })
    .describe(
      "An array of relevant keywords, topics, and concepts from the text",
    ),
});

// Schema for the response structure
export const questionOptionsSchema = z.object({
  options: z.array(
    z.object({
      questionId: z.string(),
      incorrectOptions: z
        .array(z.string())
        .length(3)
        .describe("Array of three incorrect but plausible answer options"),
    }),
  ),
});

export const generateExplanation = z.object({
  explanation: z.object({
    incorrectAnswerFeedback: z.string(),
    correctAnswer: z.string(),
    conceptExplanation: z.string(),
  }),
  memoryTrick: z.string(),
});

export type GenerateExplanationSchema = z.infer<typeof generateExplanation>;

export type QuestionStatus =
  | "Unfamiliar"
  | "Still Learning"
  | "Familiar"
  | "Mastered";

const youtubeUrlRegex =
  /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]{11}(&\S*)?$|^https?:\/\/(www\.)?youtu\.be\/[\w-]{11}(\?\S*)?$/i;

export const youtubeValidator = z.object({
  url: z.string().url().regex(youtubeUrlRegex, {
    message: "Invalid YouTube video URL",
  }),
});
export type questionsWithFileName = {
  questions: Question;
  fileName: string | null;
  sourceType: string | null;
};

export type StudySetWithAuthor = StudySet & {
  author: string;
  questionCount: number;
};
